import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  FormArray,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ProjectsService } from '@/app/services/projects/projects.service';
import { NgToastService } from 'ng-angular-popup';
import { RichTextEditorComponent } from '@/app/components/shared/rich-textEditor/rich-textEditor.component';
import { SearchLocationComponent } from '@/app/components/shared/search-location/search-location.component';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CountryComponent } from '@/app/components/global/inputs/country/country.component';
import { PlaceParserService } from '@/app/services/place-parser.service';
import { environment } from '@/app/environments/environment';
import { ProgressService } from '@/app/services/shared/progress.service';
import { SendForApprovalComponent } from '@/app/components/global/dialog-boxes/send-for-approval/send-for-approval.component';
import { MatDialog } from '@angular/material/dialog';
import { TabbedScrollerComponent } from '../../../../tabbed-scroller/tabbed-scroller.component';
import { GetFileIconUtilityService } from '@/app/services/utility/get-file-icon-utility.service';
import { DialogUtilityService } from '@/app/services/utility/dialog-utility.service';
import { ProgressBarComponent } from '../../../../progress-bar/progress-bar.component';
enum PROJECT_MESSAGES {
  PROJECT_CREATED = 'Project created successfully',
  PROJECT_UPDATED = 'Project updated successfully',
  REQUIRED_FIELDS_MISSING = 'Please fill all the required fields',
  ADDRESS_LOAD_FAILED = 'Failed to load address details',
}

// Custom validator for date checks
export function dateRangeValidator(
  projectStartDate: Date,
  projectEndDate: Date,
  existingMilestones: any[]
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const startDate = new Date(control.get('start_date')?.value);
    const endDate = new Date(control.get('end_date')?.value);

    // Check if dates are in chronological order and are not the same
    if (startDate >= endDate) {
      return {
        invalidDateOrder:
          'Start date must be before end date and they must not be the same.',
      };
    }

    // Check if start and end dates are within the project range
    if (startDate < projectStartDate || endDate > projectEndDate) {
      return {
        dateOutOfRange: 'Dates must be within the project start and end date.',
      };
    }

    // Check if the dates overlap with any existing milestones
    for (const milestone of existingMilestones) {
      const existingStartDate = new Date(milestone.start_date);
      const existingEndDate = new Date(milestone.end_date);

      // Check for overlap
      if (
        (startDate >= existingStartDate && startDate <= existingEndDate) ||
        (endDate >= existingStartDate && endDate <= existingEndDate) ||
        (startDate <= existingStartDate && endDate >= existingEndDate)
      ) {
        return {
          overlappingDates:
            'The milestone dates overlap with another milestone.',
        };
      }
    }

    return null; // Return null if no validation errors
  };
}
@Component({
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    RichTextEditorComponent,
    SearchLocationComponent,
    MatIconModule,
    CountryComponent,
    MatFormFieldModule,
    MatInputModule,
    ProgressBarComponent,
  ],
  selector: 'app-location-and-site-condition',
  templateUrl: './location-and-site-condition.component.html',
  styleUrls: ['./location-and-site-condition.component.css'],
})
export class LocationAndSiteConditionComponent implements OnInit {
  readonly dialog = inject(MatDialog);
  errorMessage = '';
  fileErrorMessage: string = ''; // Error for normal file input
  locationErrorMessage: string = '';
  projectStartDate: any;
  projectEndDate: any;
  currentProgress: number;
  @Output() changeStages: EventEmitter<any> = new EventEmitter<any>();
  @Input() projectId!: number | null;
  isLoadingNext = false;
  isLoadingSave = false;
  selectedImage: string | null = null;
  selectedLocationImage: string | null = null;
  resetRichTextEditor = false;
  resetMapState = false;
  showFileError = false;
  uploadedFiles: { name: any; file: File; preview: string }[] = [];
  uploadedLocationFiles: { name: any; file: File; preview: string }[] = [];
  projectInfo = '';
  uploadedFile: File | null = null;
  formattedValue = '';
  stakeholder: any = '';
  currencyInput = 0;
  currentImageIndex = 0;
  isFetch = false;
  fileName: any;
  form!: FormGroup;
  isDragging = false;
  files: File[] = [];
  base_path = environment.storageUrl;
  google_api_key = environment.apiKey;
  countryFromMap: string;
  countryId: number;
  currencySymbol: string;
  currency: string;
  country: any;
  deleted_attachments: number[] = [];
  progress = 0;
  storedProgress = 0;
  isStageOneSendForApproval = false;
  countryRequired: boolean = false;
  org_info: any = localStorage.getItem('org_info')
    ? JSON.parse(localStorage.getItem('org_info') as string)
    : null;

  selectedSection = 'section1';
  stage1Progress: any;
  constructor(
    private fb: FormBuilder,
    private projectsService: ProjectsService,
    private toast: NgToastService,

    private placeParserService: PlaceParserService,
    private progressService: ProgressService,
    private TabbedScrollerComponent: TabbedScrollerComponent,
    private getFileIconService: GetFileIconUtilityService,
    private dialogService: DialogUtilityService
  ) {}

  ngOnInit() {
    this.form = this.progressService.getProjectLocationSiteConditionsForm();
    this.projectDetail();
    this.loadGoogleMapsScript().then(() => {
      this.initializeStreetField();
    });
    this.progressService.progress$.subscribe((value) => {
      this.stage1Progress = value;
    });
    this.fetchCurrentProgress();
    this.countryFromMap = this.org_info?.country?.name;
  }
  fetchCurrentProgress() {
    this.currentProgress = this.progressService.getProgressValue();
    console.log(
      'this.currentProgress in location-and-site-condition',
      this.currentProgress
    );
  }

  initializeStreetField(): void {
    // Add search box functionality
    const input = document.getElementById('Street') as HTMLInputElement;
    const searchBox = new google.maps.places.SearchBox(input);

    // Listen for the search box place changes
    searchBox?.addListener('places_changed', () => {
      const places: google.maps.places.PlaceResult[] | undefined =
        searchBox?.getPlaces();

      if (places === undefined || places?.length === 0) return;

      // Get the first result's location
      const place = places[0];
      if (!place.geometry || !place.geometry.location) return;

      this.fillAddress(this, place);
    });
  }

  updateCountry(country: any) {
    this.countryRequired = false;
    this.countryFromMap = country?.name;
    this.countryId = country?.id;
    this.progressService.updateProgressMetrics('countryId', this.countryId);
  }

  loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined') {
        resolve(); // Google Maps already loaded
      } else {
        const script = document.createElement('script');
        script.src =
          'https://maps.googleapis.com/maps/api/js?key=AIzaSyA9Xi-EcZj4TrvdVQSvpheBXdKldXQhRZU&libraries=places';
        script.onload = () => resolve();
        script.onerror = (err) => reject(err);
        document.body.appendChild(script);
      }
    });
  }

  getZipCodeData = () => {
    const zipCode = this.form.get('zipCode')?.value;
    this.projectsService
      .getAddressByZipCode(zipCode, this.country ? this.country.code : 'us')
      .subscribe({
        next: (response: any) => {
          // Assuming response follows the structure in your example
          const result = response.results[0];
          this.fillAddress(this, result);
        },
        error: (err) => {
          this.toast.danger(PROJECT_MESSAGES.ADDRESS_LOAD_FAILED, 'ERROR');
          console.error('Error fetching address:', err);
        },
      });
  };

  private fillAddress(instance: any, result: any) {
    const { street, city, zipCode, latitude, longitude } =
      instance.mapApiResponseToForm(result);

    // Update the form fields
    instance.form.patchValue({
      street: street || '',
      city: city || '',
      zipCode: zipCode || '',
      latitude: latitude || '',
      longitude: longitude || '',
    });
  }

  get mileStonesGroups(): FormArray {
    return this.form.get('mileStonesData') as FormArray;
  }

  projectDetail() {
    if (this.projectId) {
      this.projectsService.getProjectBriefDetails(this.projectId).subscribe({
        next: (response: any) => {
          // console.log('The project Detail is:', response);
          if (response?.latitude) {
            this.form.controls['latitude']?.setValue(response.latitude);
          }
          if (response?.longitude) {
            this.form.controls['longitude']?.setValue(response.longitude);
          }
          if (response?.site_conditions) {
            this.form.controls['siteconditions']?.setValue(
              response.site_conditions
            );
          }
          if (response?.city) {
            this.form.controls['city']?.setValue(response.city);
          }
          if (response?.street) {
            this.form.controls['street']?.setValue(response.street);
          }
          if (response?.zip) {
            this.form.controls['zipCode']?.setValue(response.zip);
          }
          if (response?.country?.name) {
            this.countryFromMap = response.country.name;
          }
          if (response?.country?.id) {
            this.countryId = response.country.id;
          }

          if (response?.parent_document) {
            this.uploadedFiles = response.parent_document
              .filter((file: any) => file.type == 'site_conditions')
              .map((file: any) => ({
                file: file.file_path,
                name: file.title,
                id: file.id,
                preview: file.file_path,
              }));

            this.uploadedLocationFiles = response.parent_document
              .filter((file: any) => file.type == 'location')
              .map((file: any) => ({
                file: file.file_path,
                name: file.title,
                id: file.id,
                preview: file.file_path,
              }));
          }

          if (response.stage_one_completion_percentage) {
            this.progress = response.stage_one_completion_percentage;
            this.storedProgress = response.stage_one_completion_percentage;
          }

          if (response.stage_two_completion_percentage) {
            const progress = response.stage_two_completion_percentage;
            this.progressService.updateProgress2(progress);
          }

          if (response.stage_three_completion_percentage) {
            const progress = response.stage_three_completion_percentage;
            this.progressService.updateProgress3(progress);
          }
        },
        error: (error: any) => {
          console.error('Error fetching the project detail:', error);
        },
      });
    } else {
      // console.log('creating new project');
    }
  }

  preventSubmit(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent form submission
    }
  }

  openPdf(imageSrc: string): void {
    window.open(imageSrc, '_blank');
  }

  openPreview(index: number): void {
    this.currentImageIndex = index;
    this.selectedImage = this.uploadedFiles[index].preview;
  }

  closePreview(): void {
    this.selectedImage = null; // Reset the selected image to close the preview
  }

  prevImage(): void {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
      this.selectedImage = this.uploadedFiles[this.currentImageIndex].preview;
    }
  }

  nextImage(): void {
    if (this.currentImageIndex < this.uploadedFiles.length - 1) {
      this.currentImageIndex++;
      this.selectedImage = this.uploadedFiles[this.currentImageIndex].preview;
    }
  }

  openLocationPreview(index: number): void {
    return;
    this.currentImageIndex = index;
    this.selectedLocationImage = this.uploadedLocationFiles[index].preview;
  }

  prevLocationImage(): void {
    if (this.currentImageIndex > 0) {
      this.currentImageIndex--;
      this.selectedLocationImage =
        this.uploadedLocationFiles[this.currentImageIndex].preview;
    }
  }

  nextLocationImage(): void {
    if (this.currentImageIndex < this.uploadedLocationFiles.length - 1) {
      this.currentImageIndex++;
      this.selectedLocationImage =
        this.uploadedLocationFiles[this.currentImageIndex].preview;
    }
  }

  closeLocationPreview(): void {
    this.selectedLocationImage = null; // Reset the selected image to close the preview
  }

  /* 
  Drag and Drop function
  */
  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent, location = false) {
    console.log('The location file is:', event.dataTransfer.files);
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer?.files) {
      this.onFileUpload(event, location, true);
    }
  }
  lastUploadedFileName = ''; // Declare a variable to store the last uploaded file name
  handleFiles(fileList: FileList) {
    for (let i = 0; i < fileList.length; i++) {
      this.files = [...this.files, fileList[i]]; // Update reference for change detection
    }

    // Save the last uploaded file name
    if (fileList.length > 0) {
      this.lastUploadedFileName = fileList[fileList.length - 1].name;
    }
  }
  // Handle file upload
  onFileUpload(event: any, location = false, isDrop = false) {
    // console.log('The location and isDrop is:', location, isDrop);
    let files: any;
    if (isDrop) {
      console.log('The drop file is:', event.dataTransfer.files);
      files = event.dataTransfer.files;
    } else {
      files = event.target.files;
    }

    const targetArray = location
      ? this.uploadedLocationFiles
      : this.uploadedFiles;
    // this.errorMessage = ''; // Clear previous errors
    if (location) {
      this.locationErrorMessage = ''; // Clear previous location errors
    } else {
      this.fileErrorMessage = ''; // Clear previous file errors
    }
    for (let i = 0; i < files?.length; i++) {
      const file = files[i];

      // Check if file size is greater than 20 MB
      if (file.size > 20 * 1024 * 1024) {
        // 20 MB in bytes
        const errorMessage = `The file "${file.name}" is too large. Maximum allowed size is 20 MB.`;
        if (location) {
          this.locationErrorMessage = errorMessage;
        } else {
          this.fileErrorMessage = errorMessage;
        }
        continue;
      }

      const fileType = file.type;
      if (fileType.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          targetArray.push({
            file,
            name: file.name, // Store the file name
            preview: e.target.result,
          });
          this.progressService.updateProgressMetrics(
            'uploadedFiles',
            this.uploadedFiles
          );
          this.progressService.updateProgressMetrics(
            'uploadedLocationFiles',
            this.uploadedLocationFiles
          );
        };
        reader.readAsDataURL(file);
      } else {
        // Generate a Blob URL for PDF files
        const pdfUrl = URL.createObjectURL(file);
        targetArray.push({
          file,
          name: file.name, // Store the file name
          preview: pdfUrl,
        });
        this.progressService.updateProgressMetrics(
          'uploadedFiles',
          this.uploadedFiles
        );
        this.progressService.updateProgressMetrics(
          'uploadedLocationFiles',
          this.uploadedLocationFiles
        );
      }
    }
  }

  getFileIcon(file: {
    file: File;
    preview: string;
    id?: number;
    name?: string;
  }): string {
    return this.getFileIconService.getFileIcon(file);
  }

  removeFile(index: number, file: any) {
    this.uploadedFiles.splice(index, 1);
    this.deleted_attachments?.push(file?.id);
  }

  removeLocationFile(index: number, file: any) {
    this.uploadedLocationFiles.splice(index, 1);
    this.deleted_attachments?.push(file?.id);
  }

  addMapsStats(content: any) {
    console.log('map search result content', content);
    if (content?.geometry) {
      this.form.controls['latitude'].setValue(
        content?.geometry?.location?.lat()
      );

      this.form.controls['longitude'].setValue(
        content?.geometry?.location?.lng()
      );
    } else {
      this.form.controls['latitude'].setValue(content?.lat());

      this.form.controls['longitude'].setValue(content?.lng());
    }

    const { city, zipcode } = this.placeParserService.parsePlaceResult(content);

    this.form.controls['city'].setValue(city);
    this.form.controls['zipCode'].setValue(zipcode);
    this.form.controls['street'].setValue(content?.formatted_address);
  }

  addProjectSiteCondition(content: string) {
    this.form.controls['siteconditions'].setValue(content);
  }

  mapApiResponseToForm(result: any) {
    // Extract necessary fields from response
    const city = result.address_components.find(
      (comp: any) =>
        comp.types.includes('locality') ||
        comp.types.includes('postal_town') ||
        comp.types.includes('administrative_area_level_1')
    )?.long_name;

    const zipCode = result.address_components.find((comp: any) =>
      comp.types.includes('postal_code')
    )?.long_name;
    // console.log('result.geometry', result.geometry);
    const geometricLocation = result.geometry.location;
    let latitude = 0.0;
    let longitude = 0.0;
    if (
      typeof geometricLocation.lat === 'function' &&
      typeof geometricLocation.lng === 'function'
    ) {
      latitude = result.geometry.location.lat();
      longitude = result.geometry.location.lng();
    } else {
      latitude = result.geometry.location.lat;
      longitude = result.geometry.location.lng;
    }
    // Extract formatted address
    const street = result.formatted_address;

    return { street, city, zipCode, latitude, longitude };
  }

  allocatedAmount: any;

  async handleSubmit(action: string) {
    if (this.form.invalid || !this.projectId) {
      // Mark all fields as touched to trigger validation errors
      this.isFetch = true;
      this.toast.warning(
        PROJECT_MESSAGES.REQUIRED_FIELDS_MISSING,
        'WARNING',
        4000
      );
      this.form.markAllAsTouched();
      return;
    }
    if (action === 'next') {
      this.isLoadingNext = true;
    } else {
      this.isLoadingSave = true;
    }
    const dataToSubmit = await this.prepareDataToSubmit();

    if (dataToSubmit) {
      this.projectsService
        .updateProjectLocationAndSiteConditionsDetails(
          dataToSubmit,
          this.projectId
        )
        .subscribe({
          next: (response: any) => {
            this.toast.success(
              PROJECT_MESSAGES.PROJECT_UPDATED,
              'SUCCESS',
              4000
            );
            if (action === 'next') {
              this.isLoadingNext = false;
            } else {
              this.isLoadingSave = false;
            }

            this.processProjectAddUpdateResponse(response);
            if (action === 'next') {
              this.TabbedScrollerComponent.nextStep();
            }
          },
          error: (error: any) => {
            if (action === 'next') {
              this.isLoadingNext = false;
            } else {
              this.isLoadingSave = false;
            }
            console.error('Login failed:', error);
            this.toast.danger(error.error.message, 'DANGER', 4000);
          },
        });
      this.isFetch = true;
    }
  }

  private processProjectAddUpdateResponse(response: any) {
    // console.log('Project added successfully:', response);

    localStorage.setItem('projectId', response.data.id);
    const stages = {
      stage1: true,
      stage2: false,
      stage3: false,
    };
    localStorage.setItem('stages', JSON.stringify(stages));
    this.changeStages.emit(stages);
    this.storedProgress = response.data.stage_one_completion_percentage;
  }

  async prepareDataToSubmit() {
    const dataToSubmit = new FormData();
    // Append non-empty values only
    const appendIfNotEmpty = (key: string, value: any) => {
      if (
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== 0
      ) {
        dataToSubmit.append(key, value);
      }
    };

    if (!this.countryId) {
      this.toast.danger('Please select the country', 'ERROR', 4000);
      this.countryRequired = true;
      this.isLoadingNext = false;
      this.isLoadingSave = false;
      return false;
    } else {
      appendIfNotEmpty('country_id', this.countryId);
    }
    appendIfNotEmpty('city', this.form.get('city')?.value);
    appendIfNotEmpty('street', this.form.get('street')?.value);
    appendIfNotEmpty('zip', this.form.get('zipCode')?.value);
    appendIfNotEmpty('latitude', this.form.get('latitude')?.value);
    appendIfNotEmpty('longitude', this.form.get('longitude')?.value);
    appendIfNotEmpty('site_conditions', this.form.get('siteconditions')?.value);
    if (this.projectId) {
      this.deleted_attachments.forEach((file: any) => {
        if (file) {
          dataToSubmit.append('deleted_attachments[]', file);
        }
      });

      const site_condition_document: any = this.uploadedFiles.filter(
        (file: any) => !file.id
      );
      const location_condition_document: any =
        this.uploadedLocationFiles.filter((file: any) => !file.id);

      site_condition_document.forEach((uploadedFile: any) => {
        if (uploadedFile.file) {
          dataToSubmit.append('site_condition_documents[]', uploadedFile.file);
        }
      });

      location_condition_document.forEach((uploadedFile: any) => {
        if (uploadedFile.file) {
          dataToSubmit.append('location_attachments[]', uploadedFile.file);
        }
      });
    } else {
      this.uploadedFiles.forEach((uploadedFile) => {
        if (uploadedFile.file) {
          dataToSubmit.append('site_condition_documents[]', uploadedFile.file);
        }
      });

      this.uploadedLocationFiles.forEach((uploadedFile) => {
        if (uploadedFile.file) {
          dataToSubmit.append('location_attachments[]', uploadedFile.file);
        }
      });
    }
    return dataToSubmit;
  }

  openDialog() {
    // If progress is 100, open dialogue
    if (this.storedProgress != 100) {
      this.toast.danger(
        'All fields must be filled before sending for approval.',
        'DANGER',
        4000
      );
      return false;
    }

    /**
     * TODO: this needs to be stored in component/state/local storage
     * and will be send to send-for-approval component
     */
    const projectId = localStorage.getItem('projectId');
    if (!projectId) {
      this.toast.danger('Project id not found', 'ERROR');
      return;
    }

    const dialogRef = this.dialog.open(SendForApprovalComponent, {
      // Sending props
      data: {
        progress: this.progress,
        stage: 1,
        projectId: projectId,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        // console.log(
        //   `Approval sent successfully for project ID: ${result.projectId}`
        // );
        this.toast.success('Approval sent successfully!', 'SUCCESS', 4000);

        this.isStageOneSendForApproval = true;
        localStorage.setItem('isApproved', 'true');
      }
    });
  }

  handleBack() {
    this.TabbedScrollerComponent.previousStep();
    this.handleSubmit('save');
  }

  private updateProgressMetrics() {
    const totalFields = this.calculateTotalFields();
    const completedFields = this.calculateCompletedFields();

    // Calculate progress as a percentage
    console.log(
      'location =>> completedFields, totalFields',
      completedFields,
      totalFields
    );
    const progress = (completedFields / totalFields) * 100;
    this.progress = progress;
    this.progressService.updateProgress1(progress);
  }

  private calculateTotalFields(): number {
    let totalFields = this.getMainFormFields().length;

    if (this.form.get('mileStonesCheck')?.value) {
      const mileStonesData = this.form.get('mileStonesData') as FormArray;
      totalFields += mileStonesData.length * 3; // Each group has 3 fields
    }

    if (this.form.get('checkbox')?.value) {
      totalFields += 2; // Adding capbudget and rcbudget
    }

    totalFields += this.getAdditionalFields().length;

    return totalFields;
  }

  private calculateCompletedFields(): number {
    let completedFields = 0;

    // Check main form fields
    this.getMainFormFields().forEach((field) => {
      const value = this.form.get(field)?.value;
      if (this.shouldCountField(field, value)) {
        completedFields++;
      }
    });

    // Check milestones
    if (this.form.get('mileStonesCheck')?.value) {
      const mileStonesData = this.form.get('mileStonesData') as FormArray;
      mileStonesData.controls.forEach((group) => {
        ['name', 'start_date', 'end_date'].forEach((field) => {
          if (group.get(field)?.value) completedFields++;
        });
      });
    }

    // Check budgets
    if (this.form.get('checkbox')?.value) {
      ['capbudget', 'rcbudget'].forEach((field) => {
        if (this.form.get(field)?.value) completedFields++;
      });
    }

    // Check additional fields
    this.getAdditionalFields().forEach((field) => {
      if (field) completedFields++;
    });

    return completedFields;
  }

  private getMainFormFields(): string[] {
    return [
      'name',
      'outline',
      'city',
      'street',
      'zipCode',
      'latitude',
      'longitude',
      'siteconditions',
      'allocated',
      'startdate',
      'enddate',
    ];
  }

  private getAdditionalFields(): boolean[] {
    return [
      this.uploadedFiles.length > 0,
      this.uploadedLocationFiles.length > 0,
      !!this.countryId,
    ];
  }

  private shouldCountField(field: string, value: any): boolean {
    const tagRegex = /<[^>]*>/g;
    const cleanValue = (value: string): string =>
      value?.replace(tagRegex, '') || '';

    if (field === 'outline' || field === 'siteconditions') {
      return !!cleanValue(value);
    }
    return !!value;
  }
  closeDialog() {
    this.dialogService.closeProjectBriefDialog();
    this.handleSubmit('save');
  }
}
